import type { KeyboardEvent } from "react";
import type { Theme } from "@mui/material/styles";
import {
  DRAWER_TEXT_MAX_CHARS,
  getDrawerTextAllowedCharactersMessage,
  isDrawerTextAllowed,
  normalizeDrawerText,
} from "@components/SideDrawer/SideDrawer.schema";
import type { BulkSecurityClearanceResponse } from "@services/security-clearance/types";
import { SECURITY_CLEARANCE_BULK_MAX } from "@services/securityClearanceApi";
import type { TFunction } from "i18next";

export type BulkSecurityClearanceRow = {
  id: string;
  serialNo: string;
  taskId?: string | null;
};

export type SecurityClearanceError = Error & {
  response?: { data?: { detail?: string } };
};

type NotifyFn = (options: {
  title: string;
  description?: string;
  type: "success" | "warning" | "error";
}) => void;

export const BULK_SC_SERVICE = "bff-gatepass";
export const REASON_TEXT_MAX_CHARS = DRAWER_TEXT_MAX_CHARS;

export const notifyBulkSelectionLimit = (notify: NotifyFn, t: TFunction) => {
  notify({
    title: t("security-clearance.bulk-limit-title", { defaultValue: "Too many items selected" }),
    description: t("security-clearance.bulk-limit-detail", {
      defaultValue: "You can process up to {{max}} requests at once.",
      max: SECURITY_CLEARANCE_BULK_MAX,
    }),
    type: "warning",
  });
};

export const validateBulkDrawerReason = (
  value: string,
  reasonLabel: string,
  t: TFunction,
): string | undefined => {
  const trimmed = normalizeDrawerText(value);
  if (!trimmed) {
    return t("form-validation.required", {
      label: reasonLabel,
      defaultValue: "This field is required",
    });
  }
  if (trimmed.length > REASON_TEXT_MAX_CHARS) {
    return t("form-validation.max-length", {
      label: reasonLabel,
      max: REASON_TEXT_MAX_CHARS,
    });
  }
  if (!isDrawerTextAllowed(trimmed)) {
    return getDrawerTextAllowedCharactersMessage(t);
  }
  return undefined;
};

export const getReasonTextareaStyles = (theme: Theme) => ({
  inputSlotProps: {
    root: {
      className: "rounded-lg text-sm px-4 py-2 border",
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: { maxLength: REASON_TEXT_MAX_CHARS },
  },
  sxProps: {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": { padding: 0 },
  },
  charCounterSx: {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right" as const,
    color: "text.secondary",
  },
});

export const isPrintableDrawerKey = (event: KeyboardEvent) =>
  event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

export const notifyBulkDrawerOutcome = (
  notify: NotifyFn,
  t: TFunction,
  data: BulkSecurityClearanceResponse | undefined,
  selectedCount: number,
  messages: {
    partialTitle: string;
    partialDetailKey: string;
    partialDetailDefault: string;
    successTitle: string;
  },
) => {
  const successCount = data?.successfulCount ?? selectedCount;
  const failCount = data?.failedCount ?? 0;

  if (failCount > 0) {
    notify({
      title: messages.partialTitle,
      description: t(messages.partialDetailKey, {
        defaultValue: messages.partialDetailDefault,
        success: successCount,
        failed: failCount,
      }),
      type: "warning",
    });
    return;
  }

  notify({ title: messages.successTitle, type: "success" });
};

export const notifyBulkDrawerError = (
  notify: NotifyFn,
  t: TFunction,
  err: SecurityClearanceError,
) => {
  notify({
    title: t("common.error"),
    description: err?.response?.data?.detail ?? err?.message ?? t("common.something-went-wrong"),
    type: "error",
  });
};

export const notifyBulkTaskOutcome = (
  notify: NotifyFn,
  t: TFunction,
  result: BulkSecurityClearanceResponse | undefined,
  messages: {
    partialTitleKey: string;
    partialTitleDefault: string;
    partialDetailKey: string;
    partialDetailDefault: string;
    successTitleKey: string;
    successTitleDefault: string;
  },
) => {
  const successCount = result?.successfulCount ?? 0;
  const failCount = result?.failedCount ?? 0;

  if (failCount > 0) {
    notify({
      title: t(messages.partialTitleKey, { defaultValue: messages.partialTitleDefault }),
      description: t(messages.partialDetailKey, {
        defaultValue: messages.partialDetailDefault,
        success: successCount,
        failed: failCount,
      }),
      type: "warning",
    });
    return;
  }

  notify({
    title: t(messages.successTitleKey, { defaultValue: messages.successTitleDefault }),
    type: "success",
  });
};
